import { useBudgetingCapabilitiesQuery } from 'api/Budgeting/queries';

export interface BudgetingCapability {
  // Whether the budgeting wizard may be shown. True only when the server
  // explicitly reports the `budgeting` capability as enabled.
  isBudgetingEnabled: boolean;
  // The capability read is still in flight — callers should render a loader
  // rather than deciding to hide (so an enabled wizard doesn't flash hidden).
  isLoading: boolean;
  isError: boolean;
}

/**
 * The server-authoritative budgeting capability, read from
 * `GET .../budgeting/capabilities/`.
 *
 * This is the S9 flag-gating pattern for the team: the backend owns the flag and
 * enforces it on every write; the client calls this endpoint only to decide what
 * to display. It fails closed — an error, an in-flight read, or any
 * non-`true` value all resolve to `isBudgetingEnabled: false`, so a dark or
 * unreachable backend never reveals the wizard.
 */
export const useBudgetingCapability = (): BudgetingCapability => {
  const { data, isLoading, isError } = useBudgetingCapabilitiesQuery();

  return {
    // Strict equality: only an explicit `true` enables. `undefined` (loading /
    // error / malformed payload) is treated as disabled.
    isBudgetingEnabled: data?.budgeting === true,
    isLoading,
    isError,
  };
};
